// kDocTheme - the single house-style source for generated documents. Both the pdfMake emitter
// and the docx emitter read it, so the PDF and the Word file share one look (see
// kDocBlocksCore.ts). Stored as JSON (DocumentBuilder: the "DOCB Themes" list).
//
// Pure data, server-safe. Created 2026-09-26. Changes must be ADDITIVE.

export class kDocFontFace {
    public Family: string = "";
    // Static TTF files as data: URIs or site-relative URLs. A variable font is NOT accepted:
    // PDF engines embed it as Type3 or not at all.
    public Normal: string = "";
    public Bold: string = "";
    public Italic: string = "";
    public BoldItalic: string = "";
}

/**
 * Per-glyph font fallback (Theme.GlyphFallbacks): the characters in Chars are set in Family (a Fonts
 * entry) wherever they occur - as Word does when the text face lacks a glyph (Citira: "₂" in Aptos, not
 * Gellix). Added 2026-09-27 (WP-X1).
 */
export class kDocGlyphFallback {
    public Chars: string = "";
    public Family: string = "";
}

/** A TABLE style (Theme.TableStyles, E-01): DOCBTableStyle's shape; -1 / 0 = the theme's. Added 2026-09-28 (WP-X1). */
export class kDocTableStyle {
    public Name: string = "";
    public HairlinePt: number = -1;
    public BottomRulePt: number = -1;
    public RowPaddingPt: number = -1;
    public FontSizePt: number = 0;
    public Borderless: boolean = false;
}

/** A named colour (Theme.Swatches, E-10). Added 2026-09-28 (WP-X1). */
export class kDocSwatch {
    public Name: string = "";
    public Color: string = "";
}

/**
 * E-12: the space between two blocks by what they are (Theme.SpacingRules): "after Heading 2, before a table =
 * 10.4 pt". After / Before: a paragraph style Name ("Heading 2", "Body tight"), a kind ("heading", "paragraph",
 * "table", "list", "image", "diagram") or "*". SpacePt: the space from the end of the first block to the start of
 * the second - it REPLACES the first's space after + the second's space before (a block's own SpaceBeforePt
 * still adds). The first matching rule wins. Added 2026-09-28 (WP-X1).
 */
export class kDocSpacingRule {
    public After: string = "";
    public Before: string = "";
    public SpacePt: number = 0;
}

/**
 * A CHART style (Theme.ChartStyles, by Name) for the SVG charts X3's kDocChartSvg draws from table data into the
 * diagram path (E-05). Every default = "the renderer's own". Added 2026-09-28 (WP-X1, for X3).
 */
export class kDocChartStyle {
    public Name: string = "";
    // Series colours in order, as hex (swatch names are resolved before output).
    public SeriesColors: string[] = [];
    // "" = Theme.DiagramFontFamily.
    public Font: string = "";
    public SizePt: number = 0;
    public AxisRulePt: number = 0;
    public GridRulePt: number = 0;
    public ValueLabelSizePt: number = 0;
    public TitleRuleAbovePt: number = 0;
    // Gap between bars in % of a bar's width; the hole of a donut in % of its radius.
    public BarGapPct: number = 0;
    public DonutHolePct: number = 0;
}

/**
 * A CHARACTER style (Theme.CharacterStyles; a run names it with kDocRun.StyleName, HTML <span data-style>):
 * what it sets on the run, every field "" / 0 / false = "not set by the style". Added 2026-09-28 (WP-X1, E-06).
 */
export class kDocCharacterStyle {
    public Name: string = "";
    // A Theme.Fonts family.
    public FontFamily: string = "";
    // "" | "normal" | "bold" (a weight number >= 600 counts as bold).
    public Weight: string = "";
    // "" | "normal" | "italic".
    public Italic: string = "";
    public Color: string = "";
    // An absolute size (pt), else a factor of the surrounding size (1 = same).
    public FontSizePt: number = 0;
    public SizeScale: number = 0;
    // Letter spacing in pt; 0 = not set (use the style "normal" by not setting it).
    public LetterSpacingPt: number = 0;
    public AllCaps: boolean = false;
    public Underline: boolean = false;
    // "" | "super" | "sub".
    public Position: string = "";
    // OpenType features the style switches on, comma separated tags: "smcp" small caps, "onum" / "lnum" old-style /
    // lining figures, "tnum" / "pnum" tabular / proportional figures, "dlig" discretionary ligatures; "" = none.
    // PDF: pdfMake fontFeatures; HTML: font-feature-settings; Word: w:smallCaps, w14:numForm / numSpacing / ligatures.
    // Added 2026-10-29 (DocumentBuilder Adobe gaps Q5).
    public Features: string = "";
}

export class kDocParagraphStyle {
    public Name: string = "";
    // "" = the theme's BodyFont.
    public FontFamily: string = "";
    public FontSizePt: number = 10;
    public Bold: boolean = false;
    public Italic: boolean = false;
    public Color: string = "";
    public SpaceBeforePt: number = 0;
    public SpaceAfterPt: number = 6;
    public LineHeight: number = 1.2;
    public AllCaps: boolean = false;
    public LetterSpacingPt: number = 0;
    // ── Added 2026-09-26 (WP-X1, additive, all defaulted) ──
    // Indent of a paragraph's first line (see kDocTheme.IndentFirstParagraph).
    public FirstLineIndentPt: number = 0;
    // "" = the block's own alignment; "left" | "center" | "right" | "justify" = the style's.
    public Align: string = "";
    // A rule ABOVE the paragraph (a note heading's separator), in RuleColor; 0 = none.
    public BorderTopPt: number = 0;
    // Space between that rule and the text.
    public BorderTopGapPt: number = 0;
    // true: the space before is kept where the paragraph opens a page after a hard break (Word keeps it
    // there); false: dropped, as after a break in the flow (Theme.CollapseSpacing).
    public KeepSpaceBeforeAtPageTop: boolean = false;
    // Word's "Don't add space between paragraphs of the same style": between two consecutive blocks
    // of this style neither the first's space after nor the second's space before applies.
    public ContextualSpacing: boolean = false;
    // The rule above reaches this far past both text edges (Word draws a paragraph border outside the text).
    public BorderTopOutsetPt: number = 0;
    // Where a paragraph with a rule above OPENS a page: the rule's offset from the top of the text area
    // (negative = above it, as Word draws the border in the space-before it suppresses there).
    public BorderTopPageTopPt: number = 0;
    // A tab ("Not 3<tab>Intäkter") jumps to this offset from the left text edge; 0 = a space.
    public TabStopPt: number = 0;
    // ── Added 2026-09-26 (WP-X3): a bar UNDER the paragraph (the title bar under a page title) ──
    // Width 0 = none, -1 = the full frame width. Gap = from the text's last line to the bar.
    public RuleBelowWidthPt: number = 0;
    public RuleBelowHeightPt: number = 0;
    public RuleBelowGapPt: number = 0;
    // "" = TextColor.
    public RuleBelowColor: string = "";
    // Word spacing in % of the font's own space (InDesign's "Word Spacing", Thule 90 %): every word space
    // (U+0020) is (pct/100 - 1) x the space width wider. 100 = the font's space. The PDF draws it through the
    // pdfMake patch (style property wordSpacingPct), the breaker measures it (kDocLineStyle.WordSpacingPct),
    // the HTML sets CSS word-spacing. Added 2026-09-27 (WP-X1, requested by X2).
    public WordSpacingPct: number = 100;
    // ── Added 2026-09-27 (WP-X3b): the composed layout's paragraph composer (kDocComposeParagraphCore) ──
    // InDesign's Composer: "" or "paragraph" = the Adobe Paragraph Composer (the whole paragraph's breaks
    // chosen together), "single" = the Single-line Composer (greedy, Theme.HyphenZonePt), "renderer" = none
    // (pdfMake wraps). Composed layout only.
    public Composer: string = "";
    // InDesign's Justification ranges, used for a JUSTIFIED paragraph (ragged text is set at the desired
    // values, as InDesign does). Word spacing in % of the font's space (0 = WordSpacingPct); letter spacing
    // in % of the space width, as InDesign gives it (0 = none).
    public WordSpacingMinPct: number = 0;
    public WordSpacingMaxPct: number = 0;
    public LetterSpacingMinPct: number = 0;
    public LetterSpacingMaxPct: number = 0;
    // ── Added 2026-09-28 (WP-X1, E-11 / E-09): Word's paragraph keeps, per STYLE ──
    // Keep with next: "" = by kind (a heading keeps with what follows, other blocks do not), "on", "off". A
    // block's own override wins (kDocBlock.KeepWithNext / KeepWithNextOff). Resolved by kDocPdfStyles.KeepsWithNext.
    public KeepWithNext: string = "";
    // Keep lines together: a paragraph of this style is never split between columns (kDocBlock.KeepLinesTogether too).
    public KeepLinesTogether: boolean = false;
    // Widow / orphan control: the fewest lines on either side of a split; 0 = the engine's 2 (Word's).
    public WidowOrphanLines: number = 0;
    // E-14: the Name of the style this one is based on ("" = none). Resolved on the theme JSON, BEFORE it becomes
    // a kDocTheme (kDocStyleInheritance.ResolveThemeJson): every property this style's JSON does not state comes
    // from its base (and its base's base), as Word's "Based on". Kept for the round trip. Added 2026-09-28 (WP-X1).
    public BasedOn: string = "";
}

/** A page grid (the composed layout). Added 2026-09-26 (WP-X3). */
export class kDocGrid {
    public Name: string = "";
    public Columns: number = 1;
    public GutterPt: number = 0;
}

/**
 * A designer's ruler guide (the Pages view, DocumentBuilder 2026-10-29): a line on the pages of one grid, in page points
 * from the page's top-left. It never prints - no engine reads it; it is kept in the theme so every document using the
 * theme shows the same guides.
 */
export class kDocGuide {
    // The Name of the Theme.Grids entry whose pages show it; "" = every page.
    public Grid: string = "";
    // "x" = a vertical line at Pt from the left edge; "y" = a horizontal line at Pt from the top edge.
    public Axis: string = "x";
    public Pt: number = 0;
}

/**
 * How a page looks: background, header variant, grid. Every "" field inherits (page break ->
 * Level-2 section -> Level-1 section -> Theme.PageStyle). Added 2026-09-26 (WP-X3).
 */
export class kDocPageStyle {
    // Hex fill of the whole page.
    public Background: string = "";
    // A full-page image (cover-cropped): stored Url, resolved into DataUri before emitting.
    public BackgroundImageUrl: string = "";
    public BackgroundImageDataUri: string = "";
    // "normal" | "inverse" (Theme.HeaderInverseColor) | "none".
    public Header: string = "";
    // The Name of a Theme.Grids entry.
    public Grid: string = "";
    // The running header's TEXT moved this many points down on these pages (negative = up); its rule stays
    // where it is. 0 = as the theme places it. Unlike the fields above it does NOT inherit: every page break /
    // new-page section starts its pages at its own value. The flow layout reads it (kDocPageMap.HeaderOffsetAt);
    // Word's sections of Citira p2-3 / p9-10 set their header 7.08 pt lower. Added 2026-09-27 (WP-C6).
    public HeaderOffsetPt: number = 0;
}

/** A badge preset (risk numbers, value-chain letters). Added 2026-09-26 (WP-X3). */
export class kDocMarkerStyle {
    public Name: string = "";
    public DiameterPt: number = 12;
    public Fill: string = "#000000";
    public TextColor: string = "#ffffff";
    // "" = BodyFont.
    public FontFamily: string = "";
    public FontSizePt: number = 6;
    public Bold: boolean = false;
}

export class kDocTheme {
    public Name: string = "Default";
    public BodyFont: string = "Roboto";
    public HeadingFont: string = "Roboto";
    public Fonts: kDocFontFace[] = [];
    // Characters set in another face than their text's (kDocGlyphFallback); [] = none. A character
    // listed here is printed as itself in that face, so the built-in sub-/superscript digit and U+2011
    // substitutions (kDocPdfInline.GlyphPieces) do not apply to it. Added 2026-09-27 (WP-X1).
    public GlyphFallbacks: kDocGlyphFallback[] = [];
    // Character styles by Name (kDocRun.StyleName). Empty = none. Added 2026-09-28 (WP-X1, E-06).
    public CharacterStyles: kDocCharacterStyle[] = [];
    // Chart styles by Name (E-05; X3's kDocChartSvg). Empty = none. Added 2026-09-28 (WP-X1).
    public ChartStyles: kDocChartStyle[] = [];
    // Table styles by Name (E-01, the DOCBTableStyle shape; the engine does not read them - DOCB applies one to a
    // table's own fields). Empty = none. Added 2026-09-28 (WP-X1).
    public TableStyles: kDocTableStyle[] = [];
    // Named colours (E-10; the engine does not read them - colours are resolved to hex before output). Empty = none.
    public Swatches: kDocSwatch[] = [];
    // Spacing rules between blocks by style / kind (E-12, kDocSpacingRule); [] = the styles' own spacing.
    public SpacingRules: kDocSpacingRule[] = [];
    // The sign before a positive figure in a column with SignPositive (E-02); "+" by default. Added 2026-09-28.
    public PositiveSign: string = "+";

    public TextColor: string = "#1a1a1a";
    public PrimaryColor: string = "#0d2b4e";
    public AccentColor: string = "#0072C6";
    public MutedColor: string = "#6b6b6b";
    public RuleColor: string = "#9a9a9a";
    // Fill of a kDocTableColumn with Highlight = true (the current-year column).
    public HighlightFill: string = "#e6f0e6";

    public Body: kDocParagraphStyle = new kDocParagraphStyle();
    // Index 0 = Heading 1 ... index 3 = Heading 4.
    public Headings: kDocParagraphStyle[] = [];
    public Caption: kDocParagraphStyle = new kDocParagraphStyle();
    public TableText: kDocParagraphStyle = new kDocParagraphStyle();
    // Extra named styles referenced by kDocBlock.StyleName.
    public Named: kDocParagraphStyle[] = [];

    // Left, top, right, bottom.
    public PageMarginsMm: number[] = [20, 25, 20, 20];

    // Running header. Placeholders: {title} {section} {organisation}.
    public HeaderLeft: string = "{title}";
    public HeaderRight: string = "{section}";
    // Running footer. Placeholders: {page} {total} {organisation} {title}.
    public FooterLeft: string = "{organisation}";
    public FooterRight: string = "{page} ({total})";
    // false: the cover and TOC pages carry no header/footer and are not counted.
    // true:  the TOC pages ARE counted and furnished (the Citira annual-report layout: the TOC
    //        is "1 (56)"). The cover never carries a header or footer and is never counted.
    public NumberFrontMatter: boolean = false;
    public LogoDataUri: string = "";
    // Stored location of the logo; resolved into LogoDataUri before emitting. Added 2026-09-26.
    public LogoUrl: string = "";
    public Organisation: string = "";

    // Number output rules (Intl.NumberFormat supplies separators from the document Locale).
    public ZeroDisplay: string = "–";
    public NegativeStyle: "minus" | "parentheses" = "minus";

    // ── Added 2026-09-26 (WP-B, additive, all defaulted) ─────────────────────────────────────
    // A SPACE-LIKE thousands separator (sv-SE's no-break space) is swapped for ThinSpaceChar, the
    // typographic thin space of a financial statement. A "," (en-GB) or "." (de-DE) separator is
    // never touched.
    public ThinSpaceGrouping: boolean = true;
    // U+2009 THIN SPACE by default: Roboto (pdfMake's built-in font) HAS it, while it has NO glyph
    // for U+202F NARROW NO-BREAK SPACE (verified 2026-09-26: U+202F prints as a missing-glyph box).
    // The emitters mark number cells noWrap, so the (breaking) thin space never wraps a figure.
    // Use " " only with a brand font that has the glyph.
    public ThinSpaceChar: string = " ";
    // Ask the font for tabular (fixed-width) figures in number cells, so digits line up.
    public TabularFigures: boolean = true;
    // Running header / footer typography.
    public HeaderFontSizePt: number = 7.5;
    public HeaderAllCaps: boolean = true;
    public HeaderLetterSpacingPt: number = 1;
    // Composed "tabs" header (WP-X3, 2026-09-27; additive): the header's font family ("" = BodyFont; Thule:
    // a family whose Bold face is Suisse Intl BOLD, the body's bold is SemiBold), the sub-tab row's tracking
    // (Thule +0.06) and the page number's (Thule +0.4). Defaults keep the earlier output.
    public HeaderFont: string = "";
    public HeaderSubLetterSpacingPt: number = 0;
    public HeaderPageNumberLetterSpacingPt: number = 0;
    // The running header's word spacing in % of its font's space (kDocParagraphStyle.WordSpacingPct);
    // 100 = the font's. Citira: Word's expanded header sets each word space ~1 pt wider than the letters'
    // spacing alone gives. Added 2026-09-27 (WP-X1).
    public HeaderWordSpacingPct: number = 100;
    // A hairline under the running header.
    public HeaderRule: boolean = true;
    public FooterFontSizePt: number = 8;
    // Gap between the columns of a multi-column block.
    public ColumnGapMm: number = 6;
    // The light rule under every financial-table row (RuleColor is the heavier subtotal rule).
    public HairlineColor: string = "#d9d9d9";

    // ── Added 2026-09-26 (WP-X1: the Citira rebuild; additive, every default keeps the old output) ──
    // The character a negative figure starts with (NegativeStyle "minus"): U+2212 by default; a
    // Word-authored report often uses the plain hyphen-minus "-".
    public NegativeSign: string = "−";
    // Running header geometry. HeaderTopPt 0 = automatic (42 % of the top margin); otherwise the
    // distance from the top of the PAGE to the top of the header text.
    public HeaderTopPt: number = 0;
    public HeaderRuleWidthPt: number = 0.5;
    // Space between the header text and its rule.
    public HeaderRuleGapPt: number = 3;
    // Running footer: font family ("" = BodyFont), colour ("" = TextColor), and position (0 =
    // automatic; otherwise the distance from the top of the PAGE to the top of the footer text).
    public FooterFont: string = "";
    public FooterColor: string = "";
    public FooterTopPt: number = 0;
    // The running-title BAND: a second header line (e.g. "NOTER", centred under the header rule).
    // "" = no band. Placeholders as in the header. Not printed on the page where a section's title
    // is shown as a heading. BandTopPt = distance from the top of the page to the top of the text.
    public BandTemplate: string = "";
    public BandTopPt: number = 0;
    public BandFont: string = "";
    public BandFontSizePt: number = 10;
    public BandLetterSpacingPt: number = 0;
    public BandColor: string = "";
    public BandAllCaps: boolean = true;
    // The TOC's title is printed IN the band (on the TOC pages) instead of in the flow.
    public TocTitleInBand: boolean = false;
    // TOC entry typography: 0 = derived from the body size; pitch = baseline-to-baseline distance.
    public TocEntryFontSizePt: number = 0;
    public TocEntryPitchPt: number = 0;
    // TOC entries start this far below the top of the text area (after the title, if any).
    public TocEntriesOffsetPt: number = 0;
    // Column balancing estimates for the BODY font (kDocPdfColumns): average glyph advance and the
    // line box (ascender - descender), both in em. Roboto: 0.46 / 1.17.
    public AverageGlyphWidthEm: number = 0.46;
    public FontLineBoxEm: number = 1.17;
    // The rule between the columns of a multi-column block; 0 = none.
    public ColumnRuleWidthPt: number = 0;
    // Space below each list item (points).
    public ListItemSpacingPt: number = 2;
    // Unordered lists: "" = pdfMake's drawn dot; else this text ("•") is the bullet, in the body font,
    // with the item text at ListIndentPt (a hanging indent, Word's bullet + tab). Added 2026-09-26.
    public ListBulletText: string = "";
    // Automatic hyphenation of paragraphs and list items (kDocHyphenCore, soft hyphens, by the document
    // Locale; Swedish and English). Default off so existing documents are unaffected. Added 2026-09-26.
    public Hyphenate: boolean = false;
    // Letters that must stay before a hyphenation break; 0 = the language's (2). Larger values hyphenate
    // only where much of the word still fits (see kDocHyphenCore.HyphenateDocument).
    public HyphenMinLeft: number = 0;
    // Letters that must stay AFTER a hyphenation break; 0 = the language's (2). InDesign's "Before Last"
    // (Thule: 3). Read by the composed layout's hyphenation (kDocComposeHyphenation). Added 2026-09-27 (WP-X3b).
    public HyphenMinRight: number = 0;
    // Word's line breaking decided in the core (kDocLineBreakCore) instead of the renderer's own
    // wrapping: greedy fill, hyphenation only past HyphenZonePt (Word's hyphenation zone, 0.25" = 18 pt).
    // Needs a measurer (the browser PDF path sets one). Default off. Added 2026-09-26.
    public WordLineBreaks: boolean = false;
    public HyphenZonePt: number = 18;
    // Added to the width WordLineBreaks fits a line into (pt; negative = tighter). Word's fitting edge is
    // not exactly the geometric one (Citira: about 0.6 pt inside it, measured per page with
    // tools\examples\citira). Default 0. Added 2026-09-26 (WP-X1).
    public LineFitAdjustPt: number = 0;
    // WordLineBreaks + a JUSTIFIED paragraph: the core breaks it like any other (Word's greedy fill) and
    // stretches every line but the last at its spaces (kDocWordLines.Justify). A line that ends in a line break
    // the AUTHOR typed (Shift+Enter) is stretched too - Word's default; false = Word's compatibility option
    // "Don't expand character spaces on a line ending with SHIFT+RETURN". Added 2026-09-27 (WP-X1).
    public JustifyManualBreakLines: boolean = true;
    // Balancing a columns section's LAST page (kDocPdfColumnFiller.Balanced): the fewest lines a paragraph split may
    // leave at the foot of column 1 there (Word's orphan minimum while balancing); a paragraph that cannot keep that
    // many moves whole to column 2 (column 1 may then end short). Full pages keep the normal 2 (MinLines). 0 = the
    // normal 2 (as before). Measured on the Citira report (all 9 Word splits): balanced pages split only with >= 3 lines
    // in column 1 (p8 3/7, p28 3/3) and moved a paragraph whole where 2 would have stayed (p21, p51); full pages split
    // with 2 (p5, p22). Citira uses 3. Added 2026-09-28 (WP-X1).
    public BalanceMinLines: number = 0;
    // TOC leaders as TEXT dots (Word's tab leader: "Title ......... 12") instead of a dotted rule; needs
    // the text measurer (the browser PDF path). Added 2026-09-26 (WP-X1).
    public TocLeaderText: boolean = false;
    // Where a TOC entry's page number goes: "right" ("" = the default: flush right) | "after" (right after the
    // title: "Title  12"). Read by the composed TOC (kDocComposePlan.TocRows). Added 2026-09-28 (WP-X3b, E-25).
    public TocNumberPosition: string = "";
    public ListIndentPt: number = 0;
    public ColumnRuleColor: string = "#000000";
    // false: a paragraph right after a heading (or at the start of a flow) has no first-line indent,
    // only one that follows another paragraph does (book typography). Only matters when the style
    // has a FirstLineIndentPt.
    public IndentFirstParagraph: boolean = true;
    // Vertical offset of a section's title heading (on its own new page); a negative value lifts it
    // into the space the running-title band takes on the other pages.
    // Word's paragraph spacing: between two text blocks only the larger of space-after and
    // space-before counts (false: pdfMake's sum).
    public CollapseSpacing: boolean = false;
    public SectionTitleOffsetPt: number = 0;
    // Space below a section's title heading; -1 = the Heading 1 style's SpaceAfterPt.
    public SectionTitleSpaceAfterPt: number = -1;
    // Financial-table rules and padding (points).
    public TableHeaderRulePt: number = 0.8;
    public TableSumRulePt: number = 0.8;
    public TableHairlinePt: number = 0.4;
    public TableBottomRulePt: number = 0.4;
    // Space above and below a table.
    public TableSpaceBeforePt: number = 2;
    public TableSpaceAfterPt: number = 10;
    public TableCellPaddingPt: number = 4;
    // Right padding of the LAST column (so the figures end near the right text edge).
    public TableEdgePaddingPt: number = 1;
    public TableRowPaddingPt: number = 2.2;
    // Bottom cell padding; -1 = TableRowPaddingPt (Word sets the text low in its rows).
    public TableRowPaddingBottomPt: number = -1;
    // Line height of the header rows' (multi-line) text; 0 = TableText.LineHeight.
    public TableHeaderLineHeight: number = 0;
    // Line height of a BODY cell whose text holds a hard line break (a label wrapped as in the
    // original); its extra space comes off the row's bottom padding. 0 = TableText.LineHeight.
    public TableWrapLineHeight: number = 0;
    // Named style (Theme.Named) of a table row with Style "heading"; "" = muted italics.
    public TableHeadingStyle: string = "";
    // Line height of those heading rows; 0 = the table's. A heading style in another font has another
    // line box (Liberation Serif is ~6% shorter than Instrument Sans), so its rows come out shorter.
    public TableHeadingLineHeight: number = 0;
    // false: a "subtotal" row is bold only; the heavy rule above is drawn for "total" rows alone.
    public TableSubtotalRule: boolean = true;
    // true: the heavy header rule is drawn INTO the first body row (as Word draws a cell border), so
    // the first row sits as close under the header as the rows below each other.
    public TableHeaderRuleInset: boolean = false;
    // The cover: "standard" (logo, title, subtitle... in the flow) or "image" (the cover image fills
    // the page and the title / subtitle are placed on it at the positions below, in points from the
    // page's top-left corner).
    public CoverStyle: string = "standard";
    public CoverTitleXPt: number = 0;
    public CoverTitleTopPt: number = 0;
    public CoverTitleSizePt: number = 30;
    public CoverTitleColor: string = "";
    public CoverTitleWidthPt: number = 0;
    public CoverTitleLineHeight: number = 1;
    public CoverSubtitleXPt: number = 0;
    public CoverSubtitleTopPt: number = 0;
    public CoverSubtitleSizePt: number = 12;
    public CoverSubtitleColor: string = "";
    public CoverSubtitleFont: string = "";
    public CoverSubtitleAllCaps: boolean = false;
    public CoverSubtitleLetterSpacingPt: number = 0;
    public CoverSubtitleWidthPt: number = 0;
    // The subtitle's line height (x its font's line box), for a subtitle that wraps. 1.1 = as before.
    // Citira: Word's 14.4 pt pitch for 12 pt CitiraDisplay (box 1.155 em) = 1.039. Added 2026-09-27 (WP-X1).
    public CoverSubtitleLineHeight: number = 1.1;

    // ── Added 2026-09-26 (WP-X3: the composed page layout, DocumentBuilder\documentation\ai\
    //    DOCB-layout-spec.md; additive - "flow" keeps every existing document exactly as it was) ──
    // "flow" (kDocBlocksPdfMakeCore + kDocPdfPasses) | "composed" (kDocCompose*: grid, frames).
    public LayoutEngine: string = "flow";
    // Page grids; the first is the default. [] = one column over the live area.
    public Grids: kDocGrid[] = [];
    // Designer ruler guides (kDocGuide); display only, never printed. Added 2026-10-29.
    public Guides: kDocGuide[] = [];
    // The default page style.
    public PageStyle: kDocPageStyle = new kDocPageStyle();
    // true: the cover is counted as page 1 (it still carries no header or footer).
    public CountCover: boolean = false;
    // "text" (HeaderLeft / HeaderRight) | "tabs" (Level-1 sections as tabs, Level-2 as sub-tabs).
    public HeaderStyle: string = "text";
    // Printed before the tabs; only <b>...</b> is honoured.
    public HeaderTabsLead: string = "";
    // No-break spaces between the tabs.
    public HeaderTabSeparator: string = String.fromCharCode(160, 160, 160, 160, 160, 160);
    public HeaderSubTabSeparator: string = String.fromCharCode(160, 160, 160, 160);
    // Top of each tab row's line box, from the top of the page.
    public HeaderTabsTopPt: number = 0;
    public HeaderSubTabsTopPt: number = 0;
    // The y of each full-width header rule, and their weight.
    public HeaderTabRulesPt: number[] = [];
    public HeaderTabRuleWidthPt: number = 0.15;
    // The page number right-aligned in the header (the tabs header).
    public HeaderPageNumber: boolean = false;
    public HeaderPageNumberSizePt: number = 8;
    public HeaderPageNumberTopPt: number = 0;
    // The colour of the whole header on a page style with Header "inverse".
    public HeaderInverseColor: string = "#ffffff";
    // Badge presets for kDocMarker / kDocRun.Marker.
    public MarkerStyles: kDocMarkerStyle[] = [];
    // The fallback family for SVG text; "" = BodyFont.
    public DiagramFontFamily: string = "";
    // A diagram's Caption: "below" | "above" (a chart title over the chart).
    public DiagramCaptionPosition: string = "below";
    // The Named style of a caption "above"; "" = Caption.
    public DiagramCaptionStyle: string = "";
    // Composed layout shrink-fit: a placed frame that would spill a few lines onto a continuation page
    // may have its paragraphs' leading reduced by up to this many percent first; 0 = off.
    public MaxLeadingShrinkPct: number = 0;
}
